import Navbar from "@/components/landing/Navbar";
import Footer from "@/components/landing/Footer";
import PageHeader from "@/components/landing/PageHeader";
import SEO from "@/components/SEO";
import usePageTitle from "@/hooks/usePageTitle";

export default function PrivacyPage() {
  usePageTitle("Privacy Policy");
  return (
    <div className="bg-[var(--sci-bg)]">
      <SEO
        title="Privacy Policy"
        description="GDPR-aligned privacy policy for International Sci Ink Press Ltd. How we handle visitor, author, and reviewer data."
        path="/privacy"
        breadcrumbs={[
          { name: "Home", url: "/" },
          { name: "Privacy Policy", url: "/privacy" },
        ]}
      />
      <Navbar />
      <PageHeader
        overline="Privacy Policy"
        title={
          <>
            Editorial trust starts with{" "}
            <em className="not-italic text-[var(--sci-teal)]">privacy</em>.
          </>
        }
        sub="How International Sci Ink Press Ltd processes personal data for visitors, authors, reviewers and editors — aligned with the EU GDPR."
      />
      <section className="py-20 md:py-24">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <Block title="1. Data controller">
            <p>
              International Sci Ink Press Ltd, registration no. 205414288, registered office at Antim I 57, fl. 1, Vazrazhdane District, Sofia 1303, Bulgaria. Editorial correspondence is handled through the journal portals at{" "}
              <a href="https://www.mdpub.net" target="_blank" rel="noopener noreferrer" className="text-[var(--sci-teal)] underline">mdpub.net</a>{" "}and{" "}
              <a href="https://www.ejos.org" target="_blank" rel="noopener noreferrer" className="text-[var(--sci-teal)] underline">ejos.org</a>.
            </p>
          </Block>
          <Block title="2. What we collect on this site">
            <ul>
              <li><strong>Strictly necessary cookies</strong> — to remember your cookie-consent choice. No third-party trackers are loaded without consent.</li>
              <li><strong>Anonymous usage statistics</strong> — aggregated page-view metrics only, without IP profiling, once you accept analytics.</li>
              <li><strong>Form submissions</strong> — when you write to our editorial office your email and message are processed for correspondence purposes only.</li>
            </ul>
          </Block>
          <Block title="3. Submission data (authors / reviewers)">
            <p>
              Manuscript submission and peer review are operated on the eJManager platform under separate per-journal privacy notices linked from each journal site (mdpub.net, ejos.org). International Sci Ink Press receives only the editorial metadata required to publish and archive the article.
            </p>
          </Block>
          <Block title="4. Lawful bases">
            <ul>
              <li>Consent (Art. 6(1)(a) GDPR) for analytics cookies.</li>
              <li>Performance of a contract (Art. 6(1)(b)) for editorial workflow with authors and reviewers.</li>
              <li>Legitimate interest (Art. 6(1)(f)) for fraud prevention and basic site security.</li>
            </ul>
          </Block>
          <Block title="5. Your rights">
            <p>
              You may exercise the rights of access, rectification, erasure, restriction, portability, and objection at any time by writing to the address above. You also have the right to lodge a complaint with the Bulgarian Commission for Personal Data Protection (cpdp.bg).
            </p>
          </Block>
          <Block title="6. Retention">
            <p>
              We retain editorial correspondence for as long as is necessary for the integrity of the scholarly record. Published article metadata (DOI, authors, abstract) is retained indefinitely as part of the permanent academic record (Portico, CLOCKSS).
            </p>
          </Block>
          <Block title="7. Updates">
            <p className="font-mono-sci text-sm tracking-wider text-[var(--sci-text-muted)]">
              LAST UPDATED · JUNE 2026
            </p>
          </Block>
        </div>
      </section>
      <Footer />
    </div>
  );
}

function Block({ title, children }) {
  return (
    <section className="mb-12">
      <h2 className="font-serif-display text-2xl md:text-3xl text-[var(--sci-navy)] tracking-tight mb-4">
        {title}
      </h2>
      <div className="text-[var(--sci-text)]/85 leading-relaxed space-y-3 [&_a]:text-[var(--sci-teal)] [&_a]:underline [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:space-y-2">
        {children}
      </div>
    </section>
  );
}
